// WO 6 — Founder "use this anyway" affirm widget. Renders when an
// uploaded document was rejected by the HIGH-authenticity gate and
// the founder hasn't yet decided. Two CTAs:
//
//   PRIMARY:    "Re-upload this document"
//               → DELETE /cases/{id}/files/{file_id}/blocked-upload,
//                 then refresh case so the multi-slot tray returns.
//
//   SECONDARY:  "Use this document anyway"
//               → typed-reason modal (min 8 chars), then POST
//                 /cases/{id}/files/{file_id}/authenticity-override
//                 with stakeholder_id + slot + flag_names captured.
//                 The backend re-runs extract with the override so
//                 fields persist with a USER_INSISTED flag.
//
// Re-upload is visually the primary, use-anyway is intentionally
// de-emphasized. The reason field is mandatory (server-side too).

import { useState } from "react";
import { Loader2, AlertTriangle, RotateCcw } from "lucide-react";

export default function AuthenticityAffirmWidget({
  subjectRef, fileId, slot, flagNames, stakeholderId, busy,
  onReupload, onAffirm,
}) {
  const [openReason, setOpenReason] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const slotLabel = ({
    pan_proof: "PAN card",
    aadhaar_proof: "Aadhaar",
    photo: "Passport-style photo",
  }[slot] || slot || "this document");

  async function submitAffirm() {
    if (reason.trim().length < 8) return;
    setSubmitting(true);
    try {
      await onAffirm(reason.trim());
    } finally {
      setSubmitting(false);
      setOpenReason(false);
      setReason("");
    }
  }

  return (
    <div
      data-testid="authenticity-affirm-widget"
      className="mt-3 bg-white border border-red-300 rounded-xl p-4 shadow-sm"
    >
      <div className="flex items-start gap-2 mb-3">
        <AlertTriangle size={18} className="text-red-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider text-red-700 font-semibold mb-1">
            We can&rsquo;t verify this {slotLabel} {subjectRef ? `for ${subjectRef}` : ""}
          </div>
          <p className="text-sm text-slate-900 leading-relaxed">
            Our checks flagged something with the image you uploaded.
            Most of the time this means the photo is blurry, glared, or
            cropped &mdash; a fresh, sharper photo usually fixes it.
          </p>
          {flagNames && flagNames.length > 0 && (
            <details className="mt-2 text-xs"
                      data-testid="authenticity-affirm-flag-details">
              <summary className="text-slate-500 cursor-pointer hover:text-slate-700">
                Show the technical reasons ({flagNames.length})
              </summary>
              <ul className="mt-1.5 ml-2 list-disc list-inside text-slate-600">
                {flagNames.map((f) => (
                  <li key={f} className="font-mono text-[11px]">{f}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>

      {/* Primary action — Re-upload */}
      <button
        type="button"
        onClick={onReupload}
        disabled={busy || submitting}
        data-testid="authenticity-affirm-reupload-btn"
        className="w-full bg-[#C5283F] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#A82035] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {busy ? (
          <><Loader2 size={14} className="animate-spin" /> Working&hellip;</>
        ) : (
          <><RotateCcw size={14} /> Re-upload this document</>
        )}
      </button>

      {/* Secondary action — de-emphasized; opens typed-reason modal */}
      <div className="mt-3 pt-3 border-t border-slate-200 text-center">
        {!openReason ? (
          <button
            type="button"
            onClick={() => setOpenReason(true)}
            disabled={busy || submitting}
            data-testid="authenticity-affirm-use-anyway-link"
            className="text-xs text-slate-500 hover:text-slate-800 underline disabled:opacity-50"
          >
            My document is the original &mdash; use it anyway
          </button>
        ) : (
          <div className="text-left bg-amber-50 border border-amber-300 rounded-lg p-3"
                data-testid="authenticity-affirm-reason-modal">
            <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1.5">
              Confirm &mdash; this document IS the original
            </div>
            <p className="text-xs text-amber-900 leading-relaxed mb-2">
              The Infin team will manually verify this document before filing.
              Please type a short reason &mdash; this is recorded with your name
              and timestamp.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. The portrait on this PAN really is mine; the printer copy is faded but it's the original."
              data-testid="authenticity-affirm-reason-input"
              className="w-full text-sm border border-amber-300 rounded-md px-2 py-1.5 mb-2 font-sans"
              disabled={submitting}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setOpenReason(false); setReason(""); }}
                disabled={submitting}
                data-testid="authenticity-affirm-cancel-btn"
                className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitAffirm}
                disabled={submitting || reason.trim().length < 8}
                data-testid="authenticity-affirm-submit-btn"
                className="text-xs bg-amber-700 text-white px-3 py-1.5 rounded-md hover:bg-amber-800 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {submitting ? (
                  <><Loader2 size={12} className="animate-spin" /> Recording&hellip;</>
                ) : (
                  <>Confirm &mdash; use anyway</>
                )}
              </button>
            </div>
            <p className="mt-2 text-[10px] text-amber-700 italic">
              Note: a USER_INSISTED flag will appear prominently on the CA&rsquo;s case summary.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
